import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Code, FileText } from 'lucide-react';

interface KnowledgeViewerProps {
  knowledge: unknown;
}

export default function KnowledgeViewer({ knowledge }: KnowledgeViewerProps) {
  const [showRaw, setShowRaw] = useState(false);

  if (!knowledge) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">
        No knowledge entries yet
      </div>
    );
  }

  const entries = Array.isArray(knowledge) ? knowledge : [knowledge];
  const rawJson = JSON.stringify(knowledge, null, 2);

  return (
    <div>
      <div className="mb-3 flex items-center justify-end">
        <button
          onClick={() => setShowRaw(!showRaw)}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
        >
          {showRaw ? <FileText className="h-3.5 w-3.5" /> : <Code className="h-3.5 w-3.5" />}
          {showRaw ? 'Formatted' : 'Raw JSON'}
        </button>
      </div>

      {showRaw ? (
        <pre className="overflow-auto rounded-lg bg-slate-900 p-4 text-sm text-slate-100">
          {rawJson}
        </pre>
      ) : (
        <div className="space-y-4">
          {entries.map((entry, i) => {
            const content = typeof entry === 'string' ? entry : entry?.content ?? JSON.stringify(entry);
            const timestamp = typeof entry === 'object' && entry?.timestamp
              ? new Date(entry.timestamp as string).toLocaleString()
              : null;

            return (
              <div key={i} className="rounded-lg border border-slate-200 bg-white p-4">
                {timestamp && (
                  <p className="mb-2 text-xs text-slate-400">{timestamp}</p>
                )}
                <div className="prose prose-sm prose-slate max-w-none">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {String(content)}
                  </ReactMarkdown>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
